import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useSelectedTeam from "@/hooks/useSelectedTeam";
import LoadingSpinner from "@/components/LoadingSpinner";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import PipedriveDataMapping from "./PipedriveDataMapping";
import SkillControlsPanel from "../SkillControlsPanel";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import {
  SectionCard,
  FormRow,
  PrimaryButton,
  SecondaryButton,
  GhostButton,
  FieldInput,
  Divider,
  ArrowLeft,
  ChevronDown,
} from "@/components/ds";
import IconByName from "@/components/ds/IconByName";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

// ─── Exported nav helper ───────────────────────────────────────────────────────

export function BackToIntegrationLandingPipedrive() {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      className="flex cursor-pointer items-center gap-2 text-zinc-600 hover:text-zinc-800"
      onClick={() => navigate("/integrations")}
    >
      <ArrowLeft className="w-4 h-4" />
      Back to integrations
    </button>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface PipedriveConfig {
  id: number;
  siteId: number;
  displayName: string;
  connectedAs: string;
  companyDomain: string;
  apiTokenMasked?: string | null;
  authMethod?: string;
  oauthConnectedAt?: string | null;
  enabled: boolean;
  pushTranscripts: boolean;
  createdAt: string;
}

type TabId = "overview" | "accounts" | "ai-tools" | "field-reference" | "data-mapping" | "logs";

// ─── Capabilities ─────────────────────────────────────────────────────────────

const CAPABILITIES = [
  {
    title: "Contact and deal lookup",
    icon: "User",
    desc: "Find anyone in Pipedrive by email, phone, or name instantly in chat",
  },
  {
    title: "Deal management",
    icon: "Briefcase",
    desc: "Create, update, and close deals without leaving the conversation",
  },
  {
    title: "Activity logging",
    icon: "Edit",
    desc: "Auto-log every chat as an activity on the right contact",
  },
  {
    title: "Pipeline visibility",
    icon: "BarChart2",
    desc: "See deal stage, value, and close date during support conversations",
  },
  {
    title: "Organization intelligence",
    icon: "Building",
    desc: "Company-level view: all contacts, deals, and history at a glance",
  },
];

// ─── AI Tools ─────────────────────────────────────────────────────────────────

const AI_TOOLS = [
  { name: "pipedrive_get_contact", label: "Get contact", group: "Contacts", description: "Retrieve a contact by ID — name, email, phone, organization, and deal history." },
  { name: "pipedrive_search_contacts", label: "Search contacts", group: "Contacts", description: "Find contacts by email, phone, or name. Returns matching profiles with deal counts." },
  { name: "pipedrive_create_contact", label: "Create contact", group: "Contacts", description: "Add a new person to Pipedrive with name, email, phone, and organization link." },
  { name: "pipedrive_update_contact", label: "Update contact", group: "Contacts", description: "Update contact fields — email, phone, organization, custom fields." },

  { name: "pipedrive_get_deal", label: "Get deal", group: "Deals", description: "Full deal details: title, stage, value, owner, close date, and linked contacts." },
  { name: "pipedrive_search_deals", label: "Search deals", group: "Deals", description: "Find deals by title, contact, or organization. Returns stage and value." },
  { name: "pipedrive_create_deal", label: "Create deal", group: "Deals", description: "Create a new deal linked to a contact or organization with title, value, and stage." },
  { name: "pipedrive_update_deal", label: "Update deal", group: "Deals", description: "Update deal value, stage, close date, or custom fields." },
  { name: "pipedrive_close_deal", label: "Close deal", group: "Deals", description: "Mark a deal as Won or Lost with an optional reason." },
  { name: "pipedrive_get_deal_activities", label: "Get deal activities", group: "Deals", description: "All activities (calls, emails, tasks) logged against a deal." },

  { name: "pipedrive_get_organization", label: "Get organization", group: "Organizations", description: "Organization profile with address, contacts count, open deal count, and deal value." },
  { name: "pipedrive_search_organizations", label: "Search organizations", group: "Organizations", description: "Find organizations by name. Returns address, deal count, and linked contacts." },
  { name: "pipedrive_create_organization", label: "Create organization", group: "Organizations", description: "Add a new company to Pipedrive with name, address, and custom fields." },

  { name: "pipedrive_create_activity", label: "Create activity", group: "Activities", description: "Log a call, task, meeting, or email against a deal or contact." },
  { name: "pipedrive_get_activities", label: "Get activities", group: "Activities", description: "Retrieve upcoming or past activities for a contact or deal — type, due date, notes." },
  { name: "pipedrive_complete_activity", label: "Complete activity", group: "Activities", description: "Mark an activity as done with optional outcome note." },

  { name: "pipedrive_create_note", label: "Create note", group: "Notes", description: "Add an internal note to a deal, contact, or organization." },
  { name: "pipedrive_get_notes", label: "Get notes", group: "Notes", description: "Retrieve notes for a deal or contact — content, author, and created date." },

  { name: "pipedrive_get_pipelines", label: "Get pipelines", group: "Pipelines", description: "All sales pipelines with their names and stage counts." },
  { name: "pipedrive_get_stages", label: "Get stages", group: "Pipelines", description: "All stages in a pipeline with probability and order index." },
  { name: "pipedrive_move_deal_stage", label: "Move deal stage", group: "Pipelines", description: "Move a deal to a different pipeline stage by name or ID." },

  { name: "pipedrive_get_persons", label: "Get persons", group: "Persons", description: "List all persons in Pipedrive with pagination — name, email, and organization." },
  { name: "pipedrive_get_timeline", label: "Get timeline", group: "Persons", description: "Contact activity timeline — all deals, activities, and notes in chronological order." },
];

// ─── Field Reference ──────────────────────────────────────────────────────────

const PD_FIELD_REFERENCE = [
  {
    scope: "Contact",
    scopeKey: "contact",
    fields: [
      { field: "id", label: "Contact ID", notes: "Pipedrive person ID" },
      { field: "name", label: "Full name", notes: "First + last name" },
      { field: "email", label: "Email", notes: "Primary email address" },
      { field: "phone", label: "Phone", notes: "Primary phone number" },
      { field: "org_id", label: "Organization ID", notes: "Linked organization" },
      { field: "open_deals_count", label: "Open deals", notes: "Count of active deals" },
      { field: "owner_id", label: "Owner", notes: "Assigned sales rep" },
    ],
  },
  {
    scope: "Deal",
    scopeKey: "deal",
    fields: [
      { field: "id", label: "Deal ID", notes: "Pipedrive deal ID" },
      { field: "title", label: "Deal title", notes: "Name of the deal" },
      { field: "value", label: "Deal value", notes: "Monetary value" },
      { field: "currency", label: "Currency", notes: "ISO currency code" },
      { field: "stage_id", label: "Stage", notes: "Current pipeline stage" },
      { field: "status", label: "Status", notes: "open / won / lost / deleted" },
      { field: "close_time", label: "Close date", notes: "Expected or actual close date" },
      { field: "probability", label: "Win probability", notes: "0-100 percentage" },
    ],
  },
  {
    scope: "Organization",
    scopeKey: "organization",
    fields: [
      { field: "id", label: "Org ID", notes: "Pipedrive organization ID" },
      { field: "name", label: "Company name", notes: "Organization display name" },
      { field: "address", label: "Address", notes: "Full address string" },
      { field: "open_deals_count", label: "Open deals", notes: "Active deal count" },
      { field: "people_count", label: "Contact count", notes: "Linked contacts count" },
    ],
  },
];

// ─── Shared UI ────────────────────────────────────────────────────────────────

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="bg-zinc-900 text-zinc-100 rounded-md p-3 text-xs font-mono overflow-x-auto whitespace-pre leading-relaxed mt-2">
      {children}
    </pre>
  );
}

function CopyVariableButton({ variable }: { variable: string }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(`{${variable}}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ok */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy {${variable}}`}
      className={`text-[10px] px-1.5 py-0.5 rounded-md border font-mono transition-colors ${copied ? "border-green-300 text-green-700" : "border-zinc-200 text-zinc-500 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)]"}`}
      aria-label={`Copy variable {${variable}}`}
    >
      {copied ? "Copied" : `{${variable}}`}
    </button>
  );
}

// ─── FindKeyAccordion ─────────────────────────────────────────────────────────

function FindKeyAccordion() {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-zinc-200 overflow-hidden mt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 text-left text-sm font-medium text-zinc-700 hover:text-zinc-900"
        aria-expanded={open}
      >
        <span>Where to find your personal API token</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-4 pt-3 text-sm text-zinc-600 space-y-1.5">
          <Divider />
          <ol className="list-none space-y-2">
            {[
              "Log in to Pipedrive at app.pipedrive.com",
              "Click your avatar (top-right), then Personal preferences",
              "Go to the API tab",
              "Copy your personal API token",
            ].map((step, i) => (
              <li key={i} className="flex items-start gap-2.5">
                <span className="flex-shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center mt-0.5 bg-[color:var(--color-primary)]">
                  {i + 1}
                </span>
                <span>{step}</span>
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

// ─── CapabilitiesPanel ────────────────────────────────────────────────────────

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  return (
    <SectionCard
      className="mb-4"
      title="What's included"
      description={`${CAPABILITIES.length} feature areas, 24 AI tools — all activate on connect`}
      action={
        <GhostButton onClick={() => setOpen(!open)} aria-expanded={open}>
          {open ? "Hide details" : "Show details"}
        </GhostButton>
      }
    >
      {open && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {CAPABILITIES.map((cap, i) => (
            <div key={i} className="rounded-md border border-zinc-200 p-4">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-zinc-600">
                  <IconByName name={cap.icon} className="w-5 h-5" />
                </span>
                <span className="text-sm font-medium text-zinc-700">{cap.title}</span>
              </div>
              <p className="text-xs text-zinc-500">{cap.desc}</p>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}

// ─── HowItWorksCard ───────────────────────────────────────────────────────────

function HowItWorksCard() {
  const steps = [
    { n: 1, title: "Connect Pipedrive", desc: "Click \"Connect Pipedrive\" and authorize via OAuth — or use a legacy API token." },
    { n: 2, title: "24 AI tools activate", desc: "Contacts, deals, pipelines, activities, notes — all AI-searchable in real time." },
    { n: 3, title: "AI-powered CRM support", desc: "Agents see full deal context during every chat — no tab switching, no manual lookups." },
  ];
  return (
    <SectionCard title="How Pipedrive works in Velaro" description="Connect once — every contact, deal, and pipeline becomes AI-searchable instantly.">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {steps.map((s) => (
          <div key={s.n} className="rounded-md border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <span className="w-6 h-6 rounded-full bg-[color:var(--color-primary)] text-white text-xs font-bold flex items-center justify-center flex-shrink-0">
                {s.n}
              </span>
              <span className="font-semibold text-zinc-700 text-sm">{s.title}</span>
            </div>
            <p className="text-xs text-zinc-500">{s.desc}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

// ─── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  hasConnection,
  onConnect,
  onOpenWizard,
}: {
  hasConnection: boolean;
  onConnect(): void;
  onOpenWizard(): void;
}) {
  return (
    <div className="space-y-4">
      <CapabilitiesPanel />
      <HowItWorksCard />
      {!hasConnection ? (
        <SectionCard
          title="Connect your Pipedrive account"
          description="Enter your API token and all your contacts, deals, and pipeline data become instantly searchable by the AI during any conversation."
          action={<PrimaryButton onClick={onConnect}>Connect now</PrimaryButton>}
        />
      ) : (
        <SectionCard
          title="Launch starter workflow wizard"
          description="Build a Pipedrive CRM bot in minutes using a pre-built template — deal lookup, lead capture, or activity logging."
          action={<PrimaryButton onClick={onOpenWizard}>Launch wizard</PrimaryButton>}
        />
      )}
    </div>
  );
}

// ─── AiToolsTab ──────────────────────────────────────────────────────────────

function AiToolsTab({ hasConnection, onOpenWizard }: { hasConnection: boolean; onOpenWizard(): void }) {
  const groups = [...new Set(AI_TOOLS.map((t) => t.group))];
  return (
    <SectionCard
      title="AI tools"
      description="All tools activate automatically on connect — no configuration required."
      action={
        hasConnection ? (
          <LaunchWizardButton onClick={onOpenWizard} />
        ) : (
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-500">No connection</span>
        )
      }
    >
      <div className="space-y-6">
        {groups.map((group) => (
          <div key={group}>
            <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">{group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                <div key={tool.name} className="rounded-md border border-zinc-200 p-3">
                  <div className="flex items-start gap-2">
                    <div className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0 bg-[color:var(--color-primary)]" />
                    <div>
                      <div className="text-xs font-semibold text-zinc-700">{tool.label}</div>
                      <div className="text-[10px] font-mono text-zinc-400 mb-1">{tool.name}</div>
                      <div className="text-xs text-zinc-500">{tool.description}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Divider />
      <SkillControlsPanel integration="pipedrive" skills={AI_TOOLS} />
    </SectionCard>
  );
}

// ─── AccountsTab ──────────────────────────────────────────────────────────────

interface LegacyFormState {
  displayName: string;
  apiToken: string;
  saving: boolean;
  testing: boolean;
  error: string | null;
  testResult: { success: boolean; message: string } | null;
}

const LEGACY_FORM_DEFAULT: LegacyFormState = {
  displayName: "",
  apiToken: "",
  saving: false,
  testing: false,
  error: null,
  testResult: null,
};

function AccountsTab({
  configs,
  onRefresh,
  oauthError,
  oauthSuccess,
  onTogglePushTranscripts,
  toggleError,
}: {
  configs: PipedriveConfig[];
  onRefresh(): void;
  oauthError?: string | null;
  oauthSuccess?: boolean;
  onTogglePushTranscripts(config: PipedriveConfig): void;
  toggleError?: string | null;
}) {
  const api = useApi();
  const [deleting, setDeleting] = useState<number | null>(null);
  const [connecting, setConnecting] = useState(false);
  const [oauthFlowError, setOauthFlowError] = useState<string | null>(oauthError ?? null);
  const [showLegacy, setShowLegacy] = useState(false);
  const [form, setForm] = useState<LegacyFormState>(LEGACY_FORM_DEFAULT);

  async function handleOAuthConnect() {
    setConnecting(true);
    setOauthFlowError(null);
    try {
      const resp = await api.admin.get("Pipedrive/OAuth/AuthUrl");
      if (!resp.ok) {
        const body = (await resp.json().catch(() => ({}))) as { error?: string };
        setOauthFlowError(body?.error ?? "Failed to start OAuth flow. Please try again.");
        return;
      }
      const data = (await resp.json()) as { authUrl: string };
      window.location.href = data.authUrl;
    } catch {
      setOauthFlowError("Failed to start OAuth flow. Please try again.");
    } finally {
      setConnecting(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Remove this Pipedrive connection?")) return;
    setDeleting(id);
    try {
      await api.messaging.delete(`Pipedrive/Configs/${id}`);
      onRefresh();
    } catch (ex) {
      console.error("Pipedrive delete failed", ex);
    } finally {
      setDeleting(null);
    }
  }

  async function handleLegacySave() {
    if (!form.apiToken.trim()) {
      setForm((f) => ({ ...f, error: "API token is required." }));
      return;
    }
    setForm((f) => ({ ...f, saving: true, error: null, testResult: null }));
    try {
      const resp = await api.messaging.post("Pipedrive/Configs", {
        displayName: form.displayName.trim() || "Pipedrive",
        apiToken: form.apiToken.trim(),
      });
      if (!resp.ok) {
        const d = await resp.json().catch(() => ({}));
        setForm((f) => ({ ...f, saving: false, error: (d as { error?: string }).error ?? "Failed to save." }));
        return;
      }
      setForm(LEGACY_FORM_DEFAULT);
      setShowLegacy(false);
      onRefresh();
    } catch (ex) {
      console.error("Pipedrive legacy save failed", ex);
      setForm((f) => ({ ...f, saving: false, error: "Network error — please try again." }));
    }
  }

  async function handleLegacyTest() {
    if (!form.apiToken.trim()) {
      setForm((f) => ({ ...f, error: "Enter an API token first." }));
      return;
    }
    setForm((f) => ({ ...f, testing: true, testResult: null, error: null }));
    try {
      const resp = await api.messaging.post("Pipedrive/Test", { apiToken: form.apiToken.trim() });
      const d = (await resp.json().catch(() => ({}))) as { valid?: boolean; error?: string; accountName?: string };
      setForm((f) => ({
        ...f,
        testing: false,
        testResult: {
          success: d.valid ?? false,
          message: d.valid ? `Connected${d.accountName ? ` — ${d.accountName}` : ""}` : (d.error ?? "Connection test failed."),
        },
      }));
    } catch (ex) {
      console.error("Pipedrive test failed", ex);
      setForm((f) => ({ ...f, testing: false, testResult: { success: false, message: "Network error during test." } }));
    }
  }

  return (
    <div className="space-y-4">
      {oauthSuccess && (
        <div className="rounded-md bg-green-50 border border-green-200 p-4 text-sm text-green-800 font-medium">
          Pipedrive connected successfully via OAuth.
        </div>
      )}

      {/* ── Primary: Connect with OAuth ── */}
      <SectionCard
        title="Connect with Pipedrive"
        description="Recommended — secure OAuth 2.0, tokens refresh automatically."
        action={
          connecting ? (
            <span className="text-sm text-zinc-500">Redirecting…</span>
          ) : (
            <PrimaryButton onClick={handleOAuthConnect}>Connect with Pipedrive</PrimaryButton>
          )
        }
      >
        <p className="text-sm text-zinc-500">
          You'll be redirected to Pipedrive to authorize Velaro, then brought back automatically. No API token to copy or manage —
          access renews every hour in the background.
        </p>

        {oauthFlowError && (
          <div className="rounded-md p-3 text-sm bg-red-50 border border-red-200 text-red-800 mt-4" role="alert">
            {oauthFlowError}
          </div>
        )}
      </SectionCard>

      {/* ── Connected accounts list ── */}
      {configs.length > 0 && (
        <SectionCard title="Connected accounts">
          <div className="space-y-3">
            {configs.map((c) => (
              <div key={c.id} className="rounded-md border border-zinc-200 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-md flex items-center justify-center text-white text-lg font-bold flex-shrink-0 bg-[color:var(--color-primary)]">
                    P
                  </div>
                  <div>
                    <div className="font-medium text-zinc-800">{c.displayName || c.connectedAs || "Pipedrive"}</div>
                    {c.authMethod === "oauth" ? (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs bg-green-100 text-green-700 font-medium px-2 py-0.5 rounded-full">OAuth</span>
                        {c.oauthConnectedAt && (
                          <span className="text-xs text-zinc-400">Connected {new Date(c.oauthConnectedAt).toLocaleDateString()}</span>
                        )}
                        {c.connectedAs && <span className="text-xs text-zinc-400">{c.connectedAs}</span>}
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-zinc-400 font-mono">{c.apiTokenMasked}</span>
                        <GhostButton
                          onClick={handleOAuthConnect}
                          title="Upgrade to OAuth for Marketplace compatibility"
                        >
                          Upgrade to OAuth
                        </GhostButton>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${c.enabled ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                  >
                    {c.enabled ? "Active" : "Disabled"}
                  </span>
                  <GhostButton onClick={() => handleDelete(c.id)} disabled={deleting === c.id}>
                    {deleting === c.id ? "Removing…" : "Remove"}
                  </GhostButton>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* ── Transcript auto-push ── */}
      {configs.length > 0 && (
        <SectionCard title="Transcript Auto-Push">
          <div className="text-sm text-zinc-500 mb-4">
            When enabled, the full chat transcript is automatically attached as a note on the matched Pipedrive person after every conversation ends.
          </div>
          {toggleError && (
            <div className="mb-3 rounded-md p-3 text-sm bg-red-50 border border-red-200 text-red-800" role="alert">
              {toggleError}
            </div>
          )}
          <div className="space-y-3">
            {configs.map((config) => (
              <div key={config.id} className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0">
                <div>
                  <div className="text-sm font-medium text-zinc-700">{config.displayName || config.connectedAs || "Pipedrive"}</div>
                  <div className="text-xs text-zinc-500 mt-0.5">
                    {config.pushTranscripts
                      ? "Transcript attached to person after each conversation"
                      : "Transcript push is off for this connection"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onTogglePushTranscripts(config)}
                  className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none flex-shrink-0 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                  aria-label={config.pushTranscripts ? "Disable transcript push" : "Enable transcript push"}
                >
                  <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
          {configs.some((c) => c.pushTranscripts && c.enabled) && (
            <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
              Auto-push active — transcripts will appear as notes on the matched Pipedrive person after each conversation.
            </div>
          )}
        </SectionCard>
      )}

      {/* ── Legacy: Personal API Token (collapsed) ── */}
      <SectionCard
        title="Personal API token"
        description="Legacy — use OAuth above unless you have a specific reason to use a token."
        action={
          <GhostButton onClick={() => setShowLegacy((v) => !v)} aria-expanded={showLegacy}>
            {showLegacy ? "Hide" : "Show"}
          </GhostButton>
        }
      >
        {showLegacy && (
          <div className="space-y-3">
            <FormRow label="Display name" hint="Optional">
              <FieldInput
                value={form.displayName}
                onChange={(e) => setForm((f) => ({ ...f, displayName: e.target.value }))}
                placeholder="e.g. My Pipedrive"
              />
            </FormRow>
            <FormRow label="Personal API token">
              <FieldInput
                type="password"
                value={form.apiToken}
                onChange={(e) => setForm((f) => ({ ...f, apiToken: e.target.value }))}
                placeholder="Your Pipedrive API token"
                autoComplete="new-password"
                className="font-mono"
              />
              <FindKeyAccordion />
            </FormRow>

            {form.error && (
              <div className="rounded-md p-3 text-sm bg-red-50 border border-red-200 text-red-800" role="alert">
                {form.error}
              </div>
            )}
            {form.testResult && (
              <div
                className={`rounded-md p-3 text-sm ${form.testResult.success ? "bg-green-50 border border-green-200 text-green-800" : "bg-red-50 border border-red-200 text-red-800"}`}
                role="status"
              >
                {form.testResult.message}
              </div>
            )}

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <SecondaryButton onClick={handleLegacyTest} disabled={form.testing || form.saving}>
                {form.testing ? "Testing…" : "Test connection"}
              </SecondaryButton>
              <PrimaryButton onClick={handleLegacySave} disabled={form.saving || form.testing}>
                {form.saving ? "Saving…" : "Save token"}
              </PrimaryButton>
            </div>
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ─── FieldReferenceTab ────────────────────────────────────────────────────────

function FieldReferenceTab() {
  const [activeScope, setActiveScope] = useState("Contact");
  const scopeData = PD_FIELD_REFERENCE.find((s) => s.scope === activeScope) ?? PD_FIELD_REFERENCE[0];
  return (
    <SectionCard title="Field reference" description="Variable names for HTTP POST nodes to push Pipedrive data to your systems.">
      <div className="rounded-md border border-zinc-200 p-4 mb-4 text-xs text-zinc-700">
        <div className="font-semibold mb-2">Example HTTP POST variables</div>
        <CodeBlock>{`{
  "contact_name":  "{pipedrive.contact.name}",
  "contact_email": "{pipedrive.contact.email}",
  "deal_title":    "{pipedrive.deal.title}",
  "deal_value":    "{pipedrive.deal.value}",
  "deal_stage":    "{pipedrive.deal.stage_id}",
  "org_name":      "{pipedrive.organization.name}"
}`}</CodeBlock>
      </div>
      <div className="flex gap-2 mb-4 flex-wrap">
        {PD_FIELD_REFERENCE.map((s) =>
          activeScope === s.scope ? (
            <PrimaryButton key={s.scope} onClick={() => setActiveScope(s.scope)}>
              {s.scope}
            </PrimaryButton>
          ) : (
            <SecondaryButton key={s.scope} onClick={() => setActiveScope(s.scope)}>
              {s.scope}
            </SecondaryButton>
          ),
        )}
      </div>
      <div className="rounded-md border border-zinc-200 overflow-hidden">
        <div className="px-4 py-2.5 text-zinc-700 text-xs font-semibold bg-zinc-50 border-b border-zinc-200">
          {scopeData.scope} fields
        </div>
        <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Field</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Label</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Variable</th>
                <th className="px-3 py-2 text-left font-semibold text-zinc-600">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {scopeData.fields.map((f, i) => (
                <tr key={i}>
                  <td className="px-3 py-2 font-mono text-[10px] text-zinc-700">{f.field}</td>
                  <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                  <td className="px-3 py-2">
                    <CopyVariableButton variable={`pipedrive.${scopeData.scopeKey}.${f.field}`} />
                  </td>
                  <td className="px-3 py-2 text-zinc-400 italic">{f.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SectionCard>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function Pipedrive() {
  const api = useApi();
  const location = useLocation();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<PipedriveConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<TabId>("overview");
  const [showWizard, setShowWizard] = useState(false);
  const [selectedConfigId, setSelectedConfigId] = useState<number>(0);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const { teamId } = useSelectedTeam();

  // Parse OAuth callback params from URL
  const urlParams = new URLSearchParams(location.search);
  const oauthConnected = urlParams.get("connected") === "true";
  const oauthErrorCode = urlParams.get("error");
  const oauthErrorDetail = urlParams.get("detail");
  const oauthError = oauthErrorCode
    ? oauthErrorDetail
      ? `${oauthErrorCode} — ${oauthErrorDetail}`
      : oauthErrorCode
    : null;

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Pipedrive/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch (ex) {
      console.error("Pipedrive fetchConfigs failed", ex);
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  useEffect(() => {
    if (oauthConnected || oauthError) {
      setActiveTab("accounts");
      navigate(location.pathname, { replace: true });
    }
  }, [oauthConnected, oauthError, navigate, location.pathname]);

  useEffect(() => {
    if (configs.length > 0 && selectedConfigId === 0) {
      setSelectedConfigId(configs.find((c) => c.enabled)?.id ?? configs[0].id);
    }
  }, [configs, selectedConfigId]);

  async function togglePushTranscripts(config: PipedriveConfig) {
    setToggleError(null);
    try {
      const resp = await api.admin.put(`Pipedrive/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts,
      });
      if (!resp.ok) {
        const body = await resp.text().catch(() => "");
        throw new Error(`${resp.status} ${body}`.trim());
      }
      const updated = await resp.json();
      setConfigs((prev) => prev.map((c) => (c.id === config.id ? { ...c, pushTranscripts: updated.pushTranscripts } : c)));
    } catch (ex) {
      console.error("Pipedrive togglePushTranscripts failed", ex);
      setToggleError(`Failed to update transcript push setting: ${ex instanceof Error ? ex.message : String(ex)}`);
    }
  }

  const hasActiveConfig = configs.some((c) => c.enabled);

  const tabsJSX = (
    <>
      <IntegrationTab active={activeTab === "overview"} onClick={() => setActiveTab("overview")}>Overview</IntegrationTab>
      <IntegrationTab active={activeTab === "accounts"} onClick={() => setActiveTab("accounts")}>
        Accounts{configs.length > 0 && <span className="ml-1.5 text-xs bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded-full">{configs.length}</span>}
      </IntegrationTab>
      <IntegrationTab active={activeTab === "ai-tools"} onClick={() => setActiveTab("ai-tools")}>AI Skills</IntegrationTab>
      <IntegrationTab active={activeTab === "field-reference"} onClick={() => setActiveTab("field-reference")}>Field Reference</IntegrationTab>
      <IntegrationTab active={activeTab === "data-mapping"} onClick={() => setActiveTab("data-mapping")}>Data Mapping</IntegrationTab>
      <IntegrationTab active={activeTab === "logs"} onClick={() => setActiveTab("logs")}>Activity Log</IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Pipedrive" tabs={tabsJSX}>
      {showWizard && <StarterWorkflowWizard integration="pipedrive" onClose={() => setShowWizard(false)} />}

      {hasActiveConfig && (
        <SectionCard
          className="mb-4"
          title="Deploy the chat widget to your site"
          description="Your Pipedrive integration is connected — grab your embed code from Deployments to go live."
          action={
            <div className="flex items-center gap-2 shrink-0">
              <Link to="/chat_window">
                <SecondaryButton>Chat window</SecondaryButton>
              </Link>
              <Link to="/deployment">
                <PrimaryButton>Get embed code</PrimaryButton>
              </Link>
            </div>
          }
        />
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : (
        <>
          {activeTab === "overview" && (
            <OverviewTab
              hasConnection={hasActiveConfig}
              onConnect={() => setActiveTab("accounts")}
              onOpenWizard={() => setShowWizard(true)}
            />
          )}
          {activeTab === "accounts" && (
            <AccountsTab
              configs={configs}
              onRefresh={fetchConfigs}
              oauthError={oauthError}
              oauthSuccess={oauthConnected}
              onTogglePushTranscripts={togglePushTranscripts}
              toggleError={toggleError}
            />
          )}
          {activeTab === "ai-tools" && <AiToolsTab hasConnection={hasActiveConfig} onOpenWizard={() => setShowWizard(true)} />}
          {activeTab === "field-reference" && <FieldReferenceTab />}
          {activeTab === "data-mapping" && (
            <SectionCard title="Data mapping — Pipedrive">
              <PipedriveDataMapping teamId={teamId ?? 0} selectedConfigId={selectedConfigId} />
            </SectionCard>
          )}
          {activeTab === "logs" && (
            <SectionCard title="Activity log">
              <IntegrationActivityLog integrationSlug="Pipedrive" />
            </SectionCard>
          )}
        </>
      )}
    </StickyRouteHeader>
  );
}
